import * as React from "react";
import { QueryRoute } from "bernie-core";
import { Layer, Viewport, ViewSmall, ViewLarge } from "bernie-view";
import { UitkLayoutFlex } from "uitk-react-layout-flex";

import DestinationSampleReviews from "./components/DestinationSampleReviews";
import { DestinationOverallRating } from "./components/DestinationOverallRating";
import DestinationUserReviewsDialog from "./components/DestinationUserReviewsDialog";
import { DestinationUserReviewsFlexModuleResult } from "typings/microserviceModels/destination-user-reviews-flex-module";
import { DestinationUserReviewsProps, JustifyContentValues } from "./typings";
import { ViewType } from "./DestinationUserReviewsEnums";
import { withStores } from "stores";
import { observer } from "mobx-react";
import { useLocalization } from "@shared-ui/localization-context";

const NAME_FOR_TRACKING = "TG.LP.Dest.UserReviews";

const DestinationUserReviews = withStores(
  "routeStore",
  "flexModuleModelStore"
)(
  observer((props: DestinationUserReviewsProps) => {
    const { flexTemplateNode, flexModuleModelStore, routeStore } = props;
    const { id, options } = flexTemplateNode;
    const model = flexModuleModelStore.getModel(id) as DestinationUserReviewsFlexModuleResult | null;

    if (!model) {
      return null;
    }

    const { destinationName, reviews, aggregateReview, countPerRating, title } = model;
    const { formatText } = useLocalization();

    if (!(destinationName && reviews?.length && aggregateReview && countPerRating)) {
      return null;
    }

    const { totalReviews, totalRatings, averageRating } = aggregateReview;

    if (!(totalReviews && totalRatings && averageRating)) {
      return null;
    }

    const toggleAllDestinationReviews = (): void => {
      const isDialogOpen = routeStore.queryParams.destDialogReviews === "open";

      if (isDialogOpen) {
        routeStore.closeDialog("destDialogReviews");
      } else {
        routeStore.updateSearch({ destDialogReviews: "open" });
      }
    };

    const getLayoutWithFlexAlignment = (justifyContent: JustifyContentValues = "end") => (
      <UitkLayoutFlex
        className="DestinationUserReviews"
        id={id}
        data-fm={options.fmId}
        justifyContent={justifyContent}
        alignItems="center"
      >
        <DestinationOverallRating
          totalReviews={totalReviews}
          avgRating={averageRating}
          toggleAllDestinationReviews={toggleAllDestinationReviews}
          nameForTracking={NAME_FOR_TRACKING}
        />
      </UitkLayoutFlex>
    );

    return (
      <>
        {model.view === ViewType.DETAIL && (
          <div className="DestinationUserReviews uitk-cell all-cell-1-1 detailView" id={id} data-fm={options.fmId}>
            <DestinationSampleReviews
              title={title}
              nameForTracking={NAME_FOR_TRACKING}
              reviews={reviews}
              destinationName={destinationName}
              totalReviews={totalReviews}
              totalRatings={totalRatings}
              countPerRating={countPerRating}
              avgRating={averageRating}
              toggleAllDestinationReviews={toggleAllDestinationReviews}
            />
          </div>
        )}

        {model.view === ViewType.SUMMARY && (
          <Viewport>
            <ViewSmall>{getLayoutWithFlexAlignment("space-between")}</ViewSmall>
            <ViewLarge>{getLayoutWithFlexAlignment()}</ViewLarge>
          </Viewport>
        )}

        <QueryRoute query={{ destDialogReviews: "open" }}>
          <Layer id="destination-reviews" onDismiss={toggleAllDestinationReviews}>
            <DestinationUserReviewsDialog
              nameForTracking={NAME_FOR_TRACKING}
              reviewDialogTitle={formatText("destinationReviews.cityReviews", destinationName)}
              reviews={reviews}
              toggleAllDestinationReviews={toggleAllDestinationReviews}
            />
          </Layer>
        </QueryRoute>
      </>
    );
  })
);

export default DestinationUserReviews;
